import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TURNSTILE_SITE_KEY } from '../config/index.js';

/**
 * Public, instance-level runtime settings the pre-sign-in pages need.
 *
 * Until the Integrations page existed, the Turnstile site key was a Vite
 * BUILD variable — switching Turnstile on or off meant a rebuild. Now the
 * System Administrator flips it in the app, so the key has to reach the
 * browser at runtime: GET /api/integrations/public (unauthenticated, only ever
 * public values). The build variable remains the fallback so an instance that
 * never saved a row behaves exactly as before.
 */
const RuntimeConfigContext = createContext(null);

const FALLBACK = { loading: true, turnstileSiteKey: TURNSTILE_SITE_KEY || null, socialProviders: [] };

export function RuntimeConfigProvider({ children }) {
  const [state, setState] = useState(FALLBACK);

  useEffect(() => {
    let active = true;
    fetch('/api/integrations/public', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { data: {} }))
      .then((res) => {
        if (!active) return;
        const d = res?.data || {};
        setState({
          loading: false,
          // A saved "off" is a real answer (null); only an unreachable endpoint
          // falls back to the build-time key.
          turnstileSiteKey: 'turnstileSiteKey' in d ? d.turnstileSiteKey || null : TURNSTILE_SITE_KEY || null,
          // Google / Facebook sign-in switched on for this instance (8207).
          socialProviders: Array.isArray(d.socialProviders) ? d.socialProviders : [],
        });
      })
      .catch(() => active && setState({ loading: false, turnstileSiteKey: TURNSTILE_SITE_KEY || null, socialProviders: [] }));
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <RuntimeConfigContext.Provider value={value}>{children}</RuntimeConfigContext.Provider>;
}

/** Safe outside a provider (tests, storybook): falls back to the build var. */
export function useRuntimeConfig() {
  return useContext(RuntimeConfigContext) || { ...FALLBACK, loading: false };
}

/** The Turnstile site key to render with, or null when the challenge is off. */
export function useTurnstileSiteKey() {
  return useRuntimeConfig().turnstileSiteKey;
}

/** Social sign-in providers switched on here: [{ key, label }], empty when none. */
export function useSocialProviders() {
  return useRuntimeConfig().socialProviders || [];
}

export default RuntimeConfigProvider;
